/**
 * ============================================
 * UCM - Ultra Car Manager
 * Storefront - API Client (Public Endpoints)
 * Supports both live API and static data mode
 * © 2024 AlbaSoft Studio - All Rights Reserved
 * ============================================
 */

import axios from 'axios';

// Detect if we're in static mode (Cloudflare Pages deployment)
const isStaticMode = (): boolean => {
  const hostname = window.location.hostname;
  // Static mode when hosted on Cloudflare Pages or any non-localhost domain
  return !hostname.includes('localhost') && !hostname.includes('127.0.0.1');
};

// API base URL - only used in development
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Base URL for static data files
const STATIC_DATA_URL = '/data';

// Base URL for static assets (images, uploads)
const ASSETS_BASE_URL = import.meta.env.VITE_ASSETS_URL || '';

/**
 * Get the full URL for an image/asset
 * Handles both relative paths (/uploads/...) and absolute URLs (http://...)
 */
export const getImageUrl = (url: string | undefined | null): string => {
  if (!url) return '/images/car-placeholder.svg';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${ASSETS_BASE_URL}${url}`;
};

/**
 * Fetch static JSON data (for production/Cloudflare Pages)
 */
const fetchStaticData = async <T>(filename: string): Promise<T | null> => {
  try {
    const response = await fetch(`${STATIC_DATA_URL}/${filename}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error(`Error loading static data ${filename}:`, error);
    return null;
  }
};

// Tenant slug from subdomain or config
const getTenantSlug = (): string => {
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return import.meta.env.VITE_TENANT_SLUG || 'demo-autonoleggio';
  }

  return subdomain;
};

// Create axios instance for public API (used only in development)
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add tenant header to all requests
api.interceptors.request.use((config) => {
  config.headers['X-Tenant-Slug'] = getTenantSlug();
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Export static mode helper
export { isStaticMode, fetchStaticData };

// ============================================
// Public API Functions
// ============================================

export interface Vehicle {
  id: string;
  brand: string;
  model: string;
  year: number;
  licensePlate: string;
  status: string;
  dailyRate: number;
  weeklyRate?: number;
  monthlyRate?: number;
  images: string[];
  category: string;
  transmission: string;
  fuelType: string;
  seats: number;
  doors: number;
  color: string;
  mileage: number;
  features: string[];
  description?: string;
}

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  slogan?: string;
  logo?: string;
  logoDark?: string;
  favicon?: string;
  description?: string;
  address?: string;
  city?: string;
  cap?: string;
  provincia?: string;
  phone?: string;
  phoneSecondary?: string;
  whatsapp?: string;
  email?: string;
  emailSales?: string;
  emailRental?: string;
  website?: string;
  googleMapsUrl?: string;
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    youtube?: string;
    linkedin?: string;
    tiktok?: string;
    whatsapp?: string;
  };
  businessHours?: string | {
    weekdays?: string;
    saturday?: string;
    sunday?: string;
  };
  // Theming
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  // SEO
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  ogImage?: string;
}

// Get tenant info for the storefront
export const getTenantInfo = async (): Promise<TenantInfo> => {
  const response = await api.get('/public/tenant');
  return response.data.data;
};

// Get all available vehicles for rent
export const getAvailableVehicles = async (params?: {
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  transmission?: string;
  fuelType?: string;
  seats?: number;
}): Promise<Vehicle[]> => {
  const response = await api.get('/public/vehicles', { params });
  return response.data.data.vehicles || response.data.data || [];
};

// Get single vehicle details
export const getVehicleById = async (id: string): Promise<Vehicle> => {
  const response = await api.get(`/public/vehicles/${id}`);
  return response.data.data;
};

// Get featured vehicles for homepage
export const getFeaturedVehicles = async (limit = 6): Promise<Vehicle[]> => {
  const response = await api.get('/public/vehicles/featured', { params: { limit } });
  return response.data.data.vehicles || response.data.data || [];
};

// Check vehicle availability for dates
export const checkAvailability = async (
  vehicleId: string,
  startDate: string,
  endDate: string
): Promise<{ available: boolean; price: number }> => {
  const response = await api.post('/public/vehicles/check-availability', {
    vehicleId,
    startDate,
    endDate,
  });
  return response.data.data;
};

// Submit rental request
export const submitRentalRequest = async (data: {
  vehicleId: string;
  startDate: string;
  endDate: string;
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    fiscalCode?: string;
  };
  notes?: string;
}): Promise<{ success: boolean; referenceNumber: string }> => {
  const response = await api.post('/public/rental-requests', data);
  return response.data.data;
};

// Submit contact form
export const submitContactForm = async (data: {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}): Promise<{ success: boolean }> => {
  const response = await api.post('/public/contact', data);
  return response.data.data;
};

export default api;
